import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import { Shield } from "lucide-react";

interface DramaMeterProps {
  score: number; // 1-10
  className?: string;
}

const getDramaLabel = (score: number): { label: string; colorClass: string; ringVar: string } => {
  if (score <= 3) return { label: "Low Risk", colorClass: "text-cyan", ringVar: "var(--cyan)" };
  if (score <= 7) return { label: "Manipulative", colorClass: "text-amber-400", ringVar: "var(--drama-mid)" };
  return { label: "Nuclear", colorClass: "text-destructive", ringVar: "var(--drama-high)" };
};

const DramaMeter = ({ score, className }: DramaMeterProps) => {
  const { label, colorClass, ringVar } = getDramaLabel(score);

  const [animatedScore, setAnimatedScore] = useState(0);

  useEffect(() => {
    const timer = requestAnimationFrame(() => {
      setAnimatedScore(score);
    });
    return () => cancelAnimationFrame(timer);
  }, [score]);

  const percentage = ((animatedScore - 1) / 9) * 100;
  const displayPercentage = Math.max(0, Math.min(100, percentage));

  return (
    <div className={cn("space-y-3", className)}>
      {/* Score ring + label — matches Dashboard Threat Level */}
      <div className="flex items-center gap-4">
        <div className="relative w-16 h-16 shrink-0">
          <svg className="absolute inset-0 w-full h-full -rotate-90" viewBox="0 0 64 64">
            <circle cx="32" cy="32" r="28" fill="none" stroke="hsl(0 0% 15%)" strokeWidth="4" />
            <circle
              cx="32" cy="32" r="28" fill="none"
              stroke={`hsl(${ringVar})`}
              strokeWidth="4"
              strokeLinecap="round"
              strokeDasharray={`${2 * Math.PI * 28}`}
              strokeDashoffset={`${2 * Math.PI * 28 * (1 - score / 10)}`}
              style={{ transition: "stroke-dashoffset 600ms ease-out" }}
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className={cn("text-lg font-bold font-mono tabular-nums", colorClass)}>
              {score}
            </span>
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <span className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-mono">
            Threat Level
          </span>
          <span className={cn("inline-flex items-center gap-1.5 text-sm font-bold uppercase font-mono tracking-wider", colorClass)}>
            <Shield className="w-3.5 h-3.5" />
            {label}
          </span>
        </div>
      </div>

      {/* Gradient bar */}
      <div className="drama-gauge">
        <div
          className="drama-gauge-indicator"
          style={{ left: `calc(${displayPercentage}% - 8px)` }}
        />
      </div>

      <div className="flex justify-between text-[10px] text-muted-foreground font-mono">
        <span>1 - Low</span>
        <span>5 - Moderate</span>
        <span>10 - Nuclear</span>
      </div>
    </div>
  );
};

export default DramaMeter;
